import React, { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const modalRootElement = document.getElementById('modal-root');

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children }) => {
  const el = useMemo(() => document.createElement('div'), []);

  useEffect(() => {
    const currentModalRoot = modalRootElement || document.body;
    if (isOpen) {
      currentModalRoot.appendChild(el);
    }
    return () => {
      if (el.parentNode === currentModalRoot) {
        currentModalRoot.removeChild(el);
      }
    };
  }, [isOpen, el, modalRootElement]);

  if (!isOpen) {
    return null;
  }

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
    boxSizing: 'border-box',
  };

  const contentStyle: React.CSSProperties = {
    backgroundColor: 'white',
    padding: '0',
    borderRadius: '16px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
    textAlign: 'center',
    width: '100%',
    maxWidth: 'min(90vw, 650px)',
    maxHeight: '90vh',
    position: 'relative',
    zIndex: 1001,
    overflow: 'auto',
    margin: 'auto',
  };

  const closeButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: '10px',
    right: '10px',
    background: 'rgba(0, 0, 0, 0.1)',
    border: 'none',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s ease',
    zIndex: 1002,
  };

  const modalComponent = (
    <div style={overlayStyle} onClick={onClose}>
      <div style={contentStyle} onClick={(e) => e.stopPropagation()}>
        <button style={closeButtonStyle} onClick={onClose} aria-label="Close modal">
          &times;
        </button>
        {children}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalComponent, el);
};

export default Modal;
